import { Injectable, Type } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of, switchMap, tap } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { UserActions, userFeature } from '@state/user';
import { MeService, ModalService, MsisdnService, StorageService } from '@core/services';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { GeneralActions } from '@state/general/general.actions';
import { InvoiceService } from '../../core/services/invoice.service';
import { VerificationService } from '@core/services/verification.service';
import { VerificationType } from '@core/enums';
import {
  SimcardSwapEsimModalComponent,
  SimcardSwapPhysicalModalComponent,
} from '@shared/components';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { saveAs } from '@core/helpers';

import { CURRENT_MSISDN_LOCALSTORAGE_KEY } from '@core/const/const';
import { ConfirmationUnblockModalComponent } from '@shared/components/confirmation-unblock-modal/confirmation-unblock-modal.component';
import { SimCardType } from '@core/contracts/data-contracts';

@Injectable()
export class GeneralEffects {
  loadUsageOnMsisdn = createEffect(() => {
    return this.store.select(userFeature.selectSelectedMsisdnNumber).pipe(
      filter((msisdn) => !!msisdn),
      concatLatestFrom(() => this.store.select(userFeature.selectIsActivated)),
      map(([_, isActivated]) =>
        isActivated
          ? GeneralActions.overviewGetUsageDetails()
          : GeneralActions.overviewClearUsageDetails()
      )
    );
  });
  loadUsageDetails = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.overviewGetUsageDetails),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getUsageDetails(msisdn).pipe(
          map((usageChartLike) => {
            const statusItems = usageChartLike.usageStatusItem;

            return GeneralActions.overviewGetUsageDetailsSuccess({
              megabytesRemaining: statusItems[0].currentAmount,
              megabytesTotal: statusItems[0].initialAmount,
              minutesRemaining: statusItems[1]?.currentAmount ?? null,
              minutesTotal: statusItems[1]?.initialAmount ?? null,
              untilNewCredit: usageChartLike.daysTillCredit,
              extraPrice: usageChartLike.extraCosts,
              isDataOnly: statusItems.length === 1,
            });
          }),
          catchError(() => of(GeneralActions.overviewGetUsageDetailsFailure()))
        )
      )
    );
  });

  loadAllInvoices = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getAllInvoices),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([action, msisdn]) =>
        this.msisdnService.getAllInvoices(msisdn).pipe(
          map((invoiceList) => GeneralActions.getAllInvoicesSuccess(invoiceList)),
          catchError(() => of(GeneralActions.getAllInvoicesFailure()))
        )
      )
    );
  });
  loadOutstandingInvoices = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getOutstandingInvoices),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      distinctUntilChanged(
        ([prevAction, prevMsisdn], [currAction, currMsisdn]) => prevMsisdn === currMsisdn
      ),
      switchMap(([action, msisdn]) =>
        this.msisdnService.getOutstandingInvoices(msisdn).pipe(
          map((invoiceList) => GeneralActions.getOutstandingInvoicesSuccess(invoiceList)),
          catchError(() => of(GeneralActions.getOutstandingInvoicesFailure()))
        )
      )
    );
  });
  payInvoice = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.payInvoice),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ invoiceId }, msisdn]) =>
        this.msisdnService.payInvoice(invoiceId, msisdn).pipe(
          tap((response) => {
            window.location.href = response.url;
          }),
          map(() => GeneralActions.payInvoiceSuccess()),
          catchError(() => of(GeneralActions.payInvoiceFailure()))
        )
      )
    );
  });
  loadPaidInvoices = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getPaidInvoices),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      distinctUntilChanged(
        ([prevAction, prevMsisdn], [currAction, currMsisdn]) => prevMsisdn === currMsisdn
      ),
      switchMap(([action, msisdn]) =>
        this.msisdnService.getPaidInvoices(msisdn).pipe(
          map((invoiceResponse) =>
            GeneralActions.getPaidInvoicesSuccess(invoiceResponse.items, invoiceResponse.totalCount)
          ),
          catchError(() => of(GeneralActions.getPaidInvoicesFailure()))
        )
      )
    );
  });
  loadAdPreferences = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getAdPreferences),
      withLatestFrom(
        this.store.select(userFeature.selectSelectedMsisdnNumber),
        this.store.select(userFeature.selectIsMsisdnActive)
      ),
      filter(([_, __, isActive]) => isActive),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getAdSettings(msisdn).pipe(
          map((payload) => GeneralActions.getAdPreferencesSuccess(payload)),
          catchError(() => of(GeneralActions.getAdPreferencesFailure()))
        )
      )
    );
  });
  updateAdPreferences = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.updateAdPreferences),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ payload }, msisdn]) =>
        this.msisdnService.updateAdSettings(msisdn, payload).pipe(
          map((_) => GeneralActions.updateAdPreferencesSuccess(payload)),
          catchError(() => of(GeneralActions.updateAdPreferencesFailure()))
        )
      )
    );
  });
  loadPrivacySettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getPrivacySettings),
      withLatestFrom(
        this.store.select(userFeature.selectSelectedMsisdnNumber),
        this.store.select(userFeature.selectIsMsisdnActive)
      ),
      filter(([_, __, isActive]) => isActive),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getPrivacyDetails(msisdn).pipe(
          map((payload) => GeneralActions.getPrivacySettingsSuccess(payload)),
          catchError(() => of(GeneralActions.getPrivacySettingsFailure()))
        )
      )
    );
  });
  getSimType = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getSimCardType),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getSimType(msisdn).pipe(
          switchMap((payload) =>
            payload.simCardType === SimCardType.Value1
              ? [
                  GeneralActions.getSimCardTypeSuccess(payload.simCardType),
                  GeneralActions.getEsimDataSuccess(
                    payload.convertedProfile,
                    payload.activationCode,
                    payload.smdpAddress
                  ),
                ]
              : [GeneralActions.getSimCardTypeSuccess(payload.simCardType)]
          ),
          catchError(() => of(GeneralActions.getSimCardTypeFailure()))
        )
      )
    );
  });

  // confirmation required actions
  loadPukCode = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getPukCode),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.verificationService
          .performActionWithVerification(VerificationType.Email)
          .pipe(map((verificationState) => ({ verificationState, msisdn })))
      ),
      switchMap(({ verificationState, msisdn }) =>
        verificationState.isVerified
          ? this.msisdnService.getPukcode(msisdn).pipe(
              map((payload) => GeneralActions.getPukCodeSuccess(payload)),
              catchError(() => of(GeneralActions.getPukCodeFailure()))
            )
          : of(GeneralActions.getPukCodeFailure())
      )
    );
  });
  replaceSimCard = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.replaceSimCard),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ isEsim }, msisdn]) =>
        this.verificationService.performActionWithVerification(VerificationType.Email).pipe(
          switchMap((verificationState) => {
            if (!verificationState.isVerified) {
              return of({
                verificationState: { ...verificationState, isVerified: false },
                isEsim,
                msisdn,
              });
            }

            const modal: Type<unknown> = isEsim
              ? SimcardSwapEsimModalComponent
              : SimcardSwapPhysicalModalComponent;

            return this.modalService.open(modal).pipe(
              map((isApproved) => ({
                verificationState: {
                  ...verificationState,
                  isVerified: isApproved,
                },
                isEsim,
                msisdn,
              }))
            );
          })
        )
      ),
      switchMap(({ verificationState, isEsim, msisdn }) =>
        verificationState.isVerified
          ? this.msisdnService.replaceSimcard(msisdn, isEsim).pipe(
              tap((response) => {
                if (response && response.url) {
                  window.location.href = response.url;
                }
              }),
              map(() => GeneralActions.replaceSimCardSuccess()),
              catchError(() => of(GeneralActions.replaceSimCardFailure()))
            )
          : of(GeneralActions.replaceSimCardFailure())
      )
    );
  });
  blockSimCard = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.blockSimCard),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.verificationService.performActionWithVerification(VerificationType.Email).pipe(
          map((verificationState) => ({
            verificationState,
            msisdn,
          }))
        )
      ),
      switchMap(({ verificationState, msisdn }) =>
        verificationState.isVerified
          ? this.modalService.open(ConfirmationModalComponent).pipe(
              map((isApproved) => ({
                verificationState: {
                  ...verificationState,
                  isVerified: isApproved ? verificationState.isVerified : isApproved,
                },
                msisdn,
              }))
            )
          : of({
              verificationState: { ...verificationState, isVerified: false },
              msisdn,
            })
      ),
      switchMap(({ verificationState, msisdn }) =>
        verificationState.isVerified
          ? this.msisdnService.blockSimcard(msisdn).pipe(
              switchMap(() => [GeneralActions.blockSimCardSuccess(), UserActions.getUserDetails()]),
              catchError(() => of(GeneralActions.blockSimCardFailure()))
            )
          : of(GeneralActions.blockSimCardFailure())
      )
    );
  });
  unblockSimCard = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.unblockSimCard),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.verificationService.performActionWithVerification(VerificationType.Email).pipe(
          map((verificationState) => ({
            verificationState,
            msisdn,
          }))
        )
      ),
      switchMap(({ verificationState, msisdn }) =>
        verificationState.isVerified
          ? this.modalService.open(ConfirmationUnblockModalComponent).pipe(
              map((isApproved) => ({
                verificationState: {
                  ...verificationState,
                  isVerified: isApproved ? verificationState.isVerified : isApproved,
                },
                msisdn,
              }))
            )
          : of({
              verificationState: { ...verificationState, isVerified: false },
              msisdn,
            })
      ),
      switchMap(({ verificationState, msisdn }) =>
        verificationState.isVerified
          ? this.msisdnService.unblockSimcard(msisdn).pipe(
              switchMap(() => [
                GeneralActions.unblockSimCardSuccess(),
                UserActions.getUserDetails(),
              ]),
              catchError(() => of(GeneralActions.unblockSimCardFailure()))
            )
          : of(GeneralActions.unblockSimCardFailure())
      )
    );
  });
  activateSimCard = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.activateSimCard),
      concatLatestFrom(() => [this.store.select(userFeature.selectCustomerId)]),
      switchMap(([_, customerId]) =>
        this.meService.activateSimcard(customerId).pipe(
          switchMap(() => [GeneralActions.activateSimCardSuccess(), UserActions.getUserDetails()]),
          catchError(() => of(GeneralActions.activateSimCardFailure()))
        )
      )
    );
  });
  afterUserDetailsUpdated = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.getUserDetailsSuccess),
      concatLatestFrom(() => [this.store.select(userFeature.selectCustomerId)]),
      map(([_, customerId]) => {
        const userData = localStorage.getItem(CURRENT_MSISDN_LOCALSTORAGE_KEY);

        if (!userData) {
          return UserActions.changeCurrentMsisdn(customerId);
        }

        const parsedData = JSON.parse(userData);
        const { customerId: cachedCustomerId } = parsedData;

        return UserActions.changeCurrentMsisdn(cachedCustomerId);
      })
    );
  });
  getNetworkSettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getNetworkSettings),
      withLatestFrom(
        this.store.select(userFeature.selectSelectedMsisdnNumber),
        this.store.select(userFeature.selectIsMsisdnActive)
      ),
      filter(([_, __, isActive]) => isActive),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getServiceSettings(msisdn).pipe(
          map((payload) => GeneralActions.getNetworkSettingsSuccess(payload)),
          catchError(() => of(GeneralActions.getNetworkSettingsFailure()))
        )
      )
    );
  });
  updateNetworkSettings = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.updateNetworkSettings),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([{ payload }, msisdn]) =>
        this.msisdnService.updateServiceSettings(msisdn, payload).pipe(
          map(() => GeneralActions.updateNetworkSettingsSuccess(payload)),
          catchError(() => of(GeneralActions.updateNetworkSettingsFailure()))
        )
      )
    );
  });
  getBankAccountInfo = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getBankAccountInfo),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getBankAccountNumber(msisdn).pipe(
          map((payload) => GeneralActions.getBankAccountInfoSuccess(payload)),
          catchError(() => of(GeneralActions.getBankAccountInfoFailure()))
        )
      )
    );
  });

  getAvailableSubscriptions = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getAvailableSubscriptions),
      withLatestFrom(
        this.store.select(userFeature.selectSelectedMsisdnNumber),
        this.store.select(userFeature.selectIsMsisdnActive)
      ),
      filter(([_, __, isActive]) => isActive),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getAvailableSubscription(msisdn).pipe(
          map((payload) => GeneralActions.getAvailableSubscriptionsSuccess(payload)),
          catchError(() => of(GeneralActions.getAvailableSubscriptionsFailure()))
        )
      )
    );
  });
  getSpecificationInvoicesPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getSpecificationInvoicePdf),
      switchMap(({ number }) =>
        this.invoiceService.getSpecificationPdf(number).pipe(
          tap((payload) => saveAs(payload, `${number}_invoice_specifications.pdf`)),
          map(() => GeneralActions.getSpecificationInvoicePdfSuccess()),
          catchError(() => of(GeneralActions.getSpecificationInvoicePdfFailure()))
        )
      )
    );
  });
  getInvoicesPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getInvoicePdf),
      switchMap(({ number }) =>
        this.invoiceService.getInvoicePdf(number).pipe(
          tap((payload) => saveAs(payload, `${number}_invoice.pdf`)),
          map(() => GeneralActions.getInvoicePdfSuccess()),
          catchError(() => of(GeneralActions.getInvoicePdfFailure()))
        )
      )
    );
  });

  loadDataOutsideTariffs = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getDataOutsideEU),
      switchMap(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap((msisdn) =>
        this.msisdnService.getDataOutsideEu(msisdn).pipe(
          map((tariffs) => GeneralActions.getDataOutsideEUSuccess(tariffs)),
          catchError(() => of(GeneralActions.getDataOutsideEUFailure()))
        )
      )
    );
  });

  requestChangeBankAccount = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.requestChangeBankAccount),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.changeBankAccount(msisdn).pipe(
          tap((payload) => (window.location.href = payload.url)),
          map((payload) => GeneralActions.requestChangeBankAccountSuccess(payload.url)),
          catchError(() => of(GeneralActions.requestChangeBankAccountFailure()))
        )
      )
    );
  });
  downloadPrivacyPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getPrivacyPdf),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) =>
        this.msisdnService.getPrivacyRecordsPdf(msisdn).pipe(
          tap((response) => saveAs(response, `${msisdn}_privacy_records.pdf`)),
          map(() => GeneralActions.getPrivacyPdfSuccess()),
          catchError(() => of(GeneralActions.getPrivacyPdfFailure()))
        )
      )
    );
  });
  getStoragePrice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GeneralActions.getStoragePrice),
      switchMap(() =>
        this.storageService.getStoragePrice().pipe(
          map((response) => GeneralActions.getStoragePriceSuccess(response.price)),
          catchError(() => of(GeneralActions.getStoragePriceFailure()))
        )
      )
    );
  });

  refreshMsisdnDependentData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeCurrentMsisdn),
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([_, msisdn]) => {
        return from([
          GeneralActions.getAvailableSubscriptions(),
          GeneralActions.getOutstandingInvoices(),
          GeneralActions.getPaidInvoices(),
          GeneralActions.getAllInvoices(),
        ]);
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly msisdnService: MsisdnService,
    private readonly meService: MeService,
    private readonly verificationService: VerificationService,
    private readonly modalService: ModalService,
    private readonly invoiceService: InvoiceService,
    private readonly storageService: StorageService
  ) {}
}
